import React from 'react'
import { pushBackStep } from './backStep'

// useDialog is the one way every sheet, panel and overlay behaves as a modal
// dialog (review 2026-10-14, accessibility 2):
// - on open, focus moves into it: an element marked data-autofocus, else its
//   heading, else the dialog itself;
// - Tab and Shift+Tab stay inside it;
// - Escape closes it, like a tap on its backdrop (pass the same guard, e.g.
//   !saving, as closable so Escape can't dismiss mid-save either);
// - the phone's back gesture closes it, like Escape (backStep.ts);
// - on close, focus goes back to whatever opened it.
// Only the topmost open dialog listens, so Escape in a sheet opened from
// another sheet closes just the top one.

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const open: symbol[] = []

export function isTopDialog(token: symbol): boolean {
  return open[open.length - 1] === token
}

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest('[hidden], [inert]') && el.getAttribute('aria-hidden') !== 'true',
  )
}

// firstFocus picks where focus lands when a dialog opens.
export function firstFocus(root: HTMLElement): HTMLElement {
  const marked = root.querySelector<HTMLElement>('[data-autofocus]')
  if (marked) return marked
  const heading = root.querySelector<HTMLElement>('h1, h2, h3')
  if (heading) {
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1')
    return heading
  }
  if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1')
  return root
}

// nextInTrap is where Tab (or Shift+Tab) goes from the focused element: round
// to the other end at either edge, or null to let the browser move as usual.
export function nextInTrap(items: HTMLElement[], current: Element | null, backwards: boolean): HTMLElement | null {
  if (items.length === 0) return null
  const first = items[0]
  const last = items[items.length - 1]
  const index = current ? items.indexOf(current as HTMLElement) : -1
  if (index === -1) return backwards ? last : first
  if (backwards && index === 0) return last
  if (!backwards && index === items.length - 1) return first
  return null
}

export function useDialog<T extends HTMLElement>(
  ref: React.RefObject<T>,
  onClose: () => void,
  { closable = true, active = true }: { closable?: boolean; active?: boolean } = {},
): void {
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose
  const closableRef = React.useRef(closable)
  closableRef.current = closable

  React.useEffect(() => {
    if (!active) return
    const token = Symbol('dialog')
    open.push(token)
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const node = ref.current
    if (node && !node.contains(document.activeElement)) {
      firstFocus(node).focus({ preventScroll: true })
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopDialog(token)) return
      if (e.key === 'Escape') {
        if (closableRef.current) {
          e.preventDefault()
          onCloseRef.current()
        }
        return
      }
      if (e.key !== 'Tab' || !ref.current) return
      const next = nextInTrap(focusables(ref.current), document.activeElement, e.shiftKey)
      if (next) {
        e.preventDefault()
        next.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const releaseBack = pushBackStep(() => {
      if (!closableRef.current) return false
      onCloseRef.current()
      return true
    })

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      releaseBack()
      const at = open.indexOf(token)
      if (at !== -1) open.splice(at, 1)
      if (opener && opener.isConnected) opener.focus({ preventScroll: true })
    }
  }, [active, ref])
}
